import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { MarketplaceStoreSelectorComponent } from '../../shared/design-system/marketplace-store-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import {
  CreditRiskPreviewRow,
  FraudRiskAssessmentRow,
  MarketplaceOptimizationApiService,
  MarketplaceRankingProfileRow,
  MarketplaceRecommendationPreviewRow,
  MarketplaceReviewRow,
  PortfolioOptimizationPreviewRow,
  PromotedPlacementRow
} from './marketplace-optimization-api.service';

type RecommendationType = 'Newest' | 'BestSelling' | 'RelatedOfferings' | 'CategoryTrending' | 'Personalized';
type ReviewFilter = 'pending' | 'all';
type PromotedPlacementKey = 'search' | 'home';

@Component({
  standalone: true,
  selector: 'ain-marketplace-optimization-admin',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    MarketplaceStoreSelectorComponent,
    OfferingSelectorComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    TabsModule,
    TagModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CheckboxModule,
    TextareaModule,
    DatePipe
  ],
  template: `
    <ain-page
      [title]="t('catalog.optimization.title')"
      [description]="t('catalog.optimization.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.optimization.help.title'"
        [descriptionKey]="'catalog.optimization.help.description'"
        [bulletKeys]="[
          'catalog.optimization.help.bullets.search',
          'catalog.optimization.help.bullets.consent',
          'catalog.optimization.help.bullets.risk'
        ]" />

      <section class="toolbar mb-3">
        <label class="store-field">
          <span>{{ t('catalog.optimization.selectStore') }}</span>
          <ain-marketplace-store-selector
            [(ngModel)]="selectedStoreId"
            (ngModelChange)="onStoreChange()" />
        </label>
      </section>

      @if (!selectedStoreId) {
        <ain-state type="empty" [title]="t('catalog.optimization.selectStore')" />
      } @else {
        <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
          <p-tablist>
            <p-tab value="ranking">{{ t('catalog.optimization.tabs.ranking') }}</p-tab>
            <p-tab value="recommendations">{{ t('catalog.optimization.tabs.recommendations') }}</p-tab>
            <p-tab value="reviews">{{ t('catalog.optimization.tabs.reviews') }}</p-tab>
            <p-tab value="promoted">{{ t('catalog.optimization.tabs.promoted') }}</p-tab>
            <p-tab value="risk">{{ t('catalog.optimization.tabs.risk') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="ranking">
              <div class="toolbar mb-3">
                <button pButton type="button" [label]="t('catalog.optimization.ranking.reload')" (click)="loadRanking()"></button>
              </div>
              @if (rankingLoading()) {
                <ain-state type="loading" [title]="t('catalog.loading')" />
              } @else if (rankingError()) {
                <ain-state type="error" [title]="t('catalog.optimization.error')" [message]="rankingError() ?? ''" />
              } @else if (rankingProfiles().length === 0) {
                <ain-state type="empty" [title]="t('catalog.optimization.ranking.empty')" />
              } @else {
                @for (profile of rankingProfiles(); track profile.id) {
                  <section class="panel mb-3">
                    <div class="panel-head">
                      <h3 class="h6 mb-0">{{ profile.name }} ({{ profile.profileKey }})</h3>
                      <p-tag [severity]="profile.isActive ? 'success' : 'secondary'"
                        [value]="profile.isActive ? 'active' : 'inactive'" />
                    </div>
                    <p class="text-muted mb-2">
                      {{ t('catalog.optimization.ranking.columns.version') }}: {{ profile.version }}
                    </p>
                    <p-table [value]="profile.rules" [paginator]="profile.rules.length > 10" [rows]="10">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ t('catalog.optimization.ranking.rules.ruleType') }}</th>
                          <th>{{ t('catalog.optimization.ranking.rules.weight') }}</th>
                          <th>{{ t('catalog.optimization.ranking.rules.displayOrder') }}</th>
                          <th>{{ t('catalog.optimization.ranking.rules.reasonCodes') }}</th>
                          <th>{{ t('catalog.optimization.columns.status') }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-rule>
                        <tr>
                          <td>{{ rule.ruleType }}</td>
                          <td>{{ rule.weight }}</td>
                          <td>{{ rule.displayOrder }}</td>
                          <td>
                            <div class="code-list">
                              <p-tag severity="info" [value]="rule.auditReference" />
                              @if (rule.parametersJson) {
                                <code class="json-snippet">{{ rule.parametersJson }}</code>
                              }
                            </div>
                          </td>
                          <td>
                            <p-tag [severity]="rule.isEnabled ? 'success' : 'secondary'"
                              [value]="rule.isEnabled ? 'enabled' : 'disabled'" />
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </section>
                }
              }
            </p-tabpanel>

            <p-tabpanel value="recommendations">
              <div class="row g-2 mb-3 align-items-end">
                <div class="col-md-3">
                  <label class="form-label">{{ t('catalog.optimization.recommendations.type') }}</label>
                  <p-select
                    class="w-100"
                    [options]="recommendationTypeOptions"
                    optionLabel="label"
                    optionValue="value"
                    [(ngModel)]="recommendationForm.recommendationType"
                    appendTo="body" />
                </div>
                <div class="col-md-2">
                  <label class="form-label">{{ t('catalog.optimization.recommendations.limit') }}</label>
                  <p-inputNumber class="w-100" [(ngModel)]="recommendationForm.limit" [min]="1" [max]="50" />
                </div>
                <div class="col-md-4">
                  <label class="form-label">{{ t('catalog.optimization.recommendations.offering') }}</label>
                  <ain-offering-selector [(ngModel)]="recommendationForm.offeringId" [showClear]="true" />
                </div>
                <div class="col-md-2">
                  <button pButton type="button"
                    [label]="t('catalog.optimization.recommendations.preview')"
                    [loading]="recommendationLoading()"
                    (click)="previewRecommendations()"></button>
                </div>
              </div>
              @if (recommendationError()) {
                <ain-state type="error" [title]="t('catalog.optimization.error')" [message]="recommendationError() ?? ''" />
              } @else if (recommendationPreview()) {
                <section class="panel mb-3">
                  <div class="code-list mb-2">
                    <span class="label">{{ t('catalog.optimization.recommendations.reasonCodes') }}</span>
                    @for (code of recommendationPreview()!.reasonCodes; track code) {
                      <p-tag severity="info" [value]="code" />
                    }
                  </div>
                  <p class="text-muted mb-2">
                    {{ recommendationPreview()!.recommendationType }}
                    · {{ recommendationPreview()!.isPersonalized ? 'personalized' : 'deterministic' }}
                    · {{ recommendationPreview()!.cacheKey }} / {{ recommendationPreview()!.cacheVersion }}
                  </p>
                  @if (recommendationPreview()!.items.length === 0) {
                    <ain-state type="empty" [title]="t('catalog.optimization.recommendations.empty')" />
                  } @else {
                    <p-table [value]="recommendationPreview()!.items" [paginator]="true" [rows]="10">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ t('catalog.optimization.recommendations.items') }}</th>
                          <th>{{ t('catalog.optimization.recommendations.reasonCodes') }}</th>
                          <th>{{ t('catalog.optimization.recommendations.price') }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-item>
                        <tr>
                          <td>{{ item.title }} ({{ item.slug }})</td>
                          <td>
                            <div class="code-list">
                              @for (code of item.reasonCodes; track code) {
                                <p-tag severity="info" [value]="code" />
                              }
                            </div>
                          </td>
                          <td>{{ item.displayPrice }} {{ item.currencyCode }}</td>
                        </tr>
                      </ng-template>
                    </p-table>
                  }
                </section>
              }
            </p-tabpanel>

            <p-tabpanel value="reviews">
              <div class="row g-2 mb-3 align-items-end">
                <div class="col-md-4">
                  <label class="form-label">{{ t('catalog.optimization.reviews.offering') }}</label>
                  <ain-offering-selector [(ngModel)]="reviewOfferingId" (ngModelChange)="loadReviews()" />
                </div>
                <div class="col-md-3">
                  <label class="form-label">{{ t('catalog.optimization.reviews.filter') }}</label>
                  <p-select
                    class="w-100"
                    [options]="reviewFilterOptions"
                    optionValue="value"
                    [(ngModel)]="reviewFilter"
                    (ngModelChange)="loadReviews()"
                    appendTo="body">
                    <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
                    <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                  </p-select>
                </div>
                <div class="col-md-3">
                  <label class="form-label">{{ t('catalog.optimization.reviews.actorReference') }}</label>
                  <input pInputText class="w-100" [(ngModel)]="moderationActorReference" />
                </div>
                <div class="col-md-2">
                  <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="loadReviews()"></button>
                </div>
              </div>
              @if (!reviewOfferingId) {
                <ain-state type="empty" [title]="t('catalog.optimization.reviews.offeringRequired')" />
              } @else if (reviewsLoading()) {
                <ain-state type="loading" [title]="t('catalog.loading')" />
              } @else if (reviewsError()) {
                <ain-state type="error" [title]="t('catalog.optimization.error')" [message]="reviewsError() ?? ''" />
              } @else if (filteredReviews().length === 0) {
                <ain-state type="empty" [title]="t('catalog.optimization.reviews.empty')" />
              } @else {
                <p-table [value]="filteredReviews()" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('catalog.optimization.reviews.rating') }}</th>
                      <th>{{ t('catalog.optimization.reviews.title') }}</th>
                      <th>{{ t('catalog.optimization.columns.status') }}</th>
                      <th>{{ t('catalog.optimization.reviews.createdAt') }}</th>
                      <th>{{ t('catalog.optimization.columns.actions') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.rating }}</td>
                      <td>
                        <strong>{{ row.title }}</strong>
                        <p class="mb-0 text-muted">{{ row.body }}</p>
                      </td>
                      <td><p-tag [severity]="moderationSeverity(row.moderationStatus)" [value]="row.moderationStatus" /></td>
                      <td>{{ row.createdAtUtc | date:'yyyy-MM-dd HH:mm' }}</td>
                      <td>
                        @if (row.moderationStatus === 'Pending') {
                          <div class="action-row">
                            <button pButton type="button" size="small"
                              [label]="t('catalog.optimization.reviews.approve')"
                              (click)="moderateReview(row, 'Approved')"></button>
                            <button pButton type="button" size="small" severity="danger"
                              [label]="t('catalog.optimization.reviews.reject')"
                              (click)="moderateReview(row, 'Rejected')"></button>
                          </div>
                        }
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </p-tabpanel>

            <p-tabpanel value="promoted">
              <div class="row g-2 mb-3 align-items-end">
                <div class="col-md-3">
                  <label class="form-label">{{ t('catalog.optimization.promoted.placementKey') }}</label>
                  <p-select
                    class="w-100"
                    [options]="placementKeyOptions"
                    optionValue="value"
                    [(ngModel)]="promotedPlacementKey"
                    (ngModelChange)="loadPromoted()"
                    appendTo="body">
                    <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
                    <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                  </p-select>
                </div>
                <div class="col-md-2">
                  <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="loadPromoted()"></button>
                </div>
              </div>
              @if (promotedLoading()) {
                <ain-state type="loading" [title]="t('catalog.loading')" />
              } @else if (promotedError()) {
                <ain-state type="error" [title]="t('catalog.optimization.error')" [message]="promotedError() ?? ''" />
              } @else if (promotedPlacements().length === 0) {
                <ain-state type="empty" [title]="t('catalog.optimization.promoted.empty')" />
              } @else {
                <p-table [value]="promotedPlacements()" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('catalog.optimization.promoted.placementKey') }}</th>
                      <th>{{ t('catalog.optimization.promoted.disclosure') }}</th>
                      <th>{{ t('catalog.optimization.promoted.offering') }}</th>
                      <th>{{ t('catalog.optimization.promoted.boost') }}</th>
                      <th>{{ t('catalog.optimization.columns.status') }}</th>
                      <th>{{ t('catalog.optimization.promoted.visible') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.placementKey }}</td>
                      <td><p-tag severity="warn" [value]="row.disclosureLabel" /></td>
                      <td>{{ row.offeringId }}</td>
                      <td>{{ row.boostScore }}</td>
                      <td>
                        <p-tag [severity]="row.isActive ? 'success' : 'secondary'"
                          [value]="row.isActive ? 'active' : 'inactive'" />
                      </td>
                      <td>
                        @if (row.visibleFromUtc || row.visibleToUtc) {
                          {{ row.visibleFromUtc | date:'yyyy-MM-dd' }} – {{ row.visibleToUtc | date:'yyyy-MM-dd' }}
                        } @else {
                          —
                        }
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </p-tabpanel>

            <p-tabpanel value="risk">
              <p class="guardrail-banner">{{ t('catalog.optimization.risk.guardrailBanner') }}</p>

              <section class="panel mb-3">
                <h3 class="h6">{{ t('catalog.optimization.risk.fraud.title') }}</h3>
                <div class="row g-2 mb-2">
                  <div class="col-md-2"><input pInputText class="w-100" [(ngModel)]="fraudForm.currencyCode" placeholder="Currency" /></div>
                  <div class="col-md-2"><p-inputNumber class="w-100" [(ngModel)]="fraudForm.amount" [min]="0" /></div>
                  <div class="col-md-2"><input pInputText class="w-100" [(ngModel)]="fraudForm.buyerCountryCode" placeholder="Buyer country" /></div>
                  <div class="col-md-2"><input pInputText class="w-100" [(ngModel)]="fraudForm.shippingCountryCode" placeholder="Ship country" /></div>
                  <div class="col-md-2"><input pInputText class="w-100" [(ngModel)]="fraudForm.paymentCountryCode" placeholder="Pay country" /></div>
                  <div class="col-md-2"><p-inputNumber class="w-100" [(ngModel)]="fraudForm.recentPaymentFailureCount" [min]="0" /></div>
                  <div class="col-md-2"><p-inputNumber class="w-100" [(ngModel)]="fraudForm.recentRefundCount" [min]="0" /></div>
                  <div class="col-md-2"><p-inputNumber class="w-100" [(ngModel)]="fraudForm.recentDisputeCount" [min]="0" /></div>
                  <div class="col-md-2">
                    <label class="d-flex align-items-center gap-2 mb-0">
                      <p-checkbox [(ngModel)]="fraudForm.isHighValue" [binary]="true" />
                      <span>{{ t('catalog.optimization.risk.fraud.highValue') }}</span>
                    </label>
                  </div>
                  <div class="col-md-2">
                    <button pButton type="button"
                      [label]="t('catalog.optimization.risk.preview')"
                      [loading]="fraudLoading()"
                      (click)="previewFraud()"></button>
                  </div>
                </div>
                @if (fraudError()) {
                  <ain-state type="error" [title]="t('catalog.optimization.error')" [message]="fraudError() ?? ''" />
                } @else if (fraudPreview()) {
                  <div class="code-list mb-2">
                    <p-tag [severity]="riskSeverity(fraudPreview()!.recommendation)" [value]="fraudPreview()!.recommendation" />
                    <span>{{ t('catalog.optimization.risk.score') }}: {{ fraudPreview()!.score }}</span>
                    @for (code of fraudPreview()!.reasonCodes; track code) {
                      <p-tag severity="info" [value]="code" />
                    }
                  </div>
                  <pre class="evidence-json">{{ formatJson(fraudPreview()!.evidenceJson) }}</pre>
                }
              </section>

              <section class="panel mb-3">
                <h3 class="h6">{{ t('catalog.optimization.risk.credit.title') }}</h3>
                @if (creditPreview()?.guardrailNote) {
                  <p class="guardrail-note">{{ creditPreview()!.guardrailNote }}</p>
                }
                <div class="row g-2 mb-2">
                  <div class="col-md-2"><p-inputNumber class="w-100" [(ngModel)]="creditForm.requestedAmount" [min]="0" /></div>
                  <div class="col-md-2"><input pInputText class="w-100" [(ngModel)]="creditForm.currencyCode" placeholder="Currency" /></div>
                  <div class="col-md-2"><p-inputNumber class="w-100" [(ngModel)]="creditForm.monthlyIncome" [min]="0" /></div>
                  <div class="col-md-2"><p-inputNumber class="w-100" [(ngModel)]="creditForm.existingDebt" [min]="0" /></div>
                  <div class="col-md-2"><p-inputNumber class="w-100" [(ngModel)]="creditForm.requestedInstallments" [min]="1" /></div>
                  <div class="col-md-2">
                    <button pButton type="button"
                      [label]="t('catalog.optimization.risk.preview')"
                      [loading]="creditLoading()"
                      (click)="previewCredit()"></button>
                  </div>
                </div>
                @if (creditError()) {
                  <ain-state type="error" [title]="t('catalog.optimization.error')" [message]="creditError() ?? ''" />
                } @else if (creditPreview()) {
                  <div class="code-list mb-2">
                    <p-tag [value]="creditPreview()!.riskBand" />
                    <span>{{ t('catalog.optimization.risk.score') }}: {{ creditPreview()!.score }}</span>
                    @for (code of creditPreview()!.reasonCodes; track code) {
                      <p-tag severity="info" [value]="code" />
                    }
                  </div>
                  <p class="guardrail-note">{{ creditPreview()!.guardrailNote }}</p>
                  <pre class="evidence-json">{{ formatJson(creditPreview()!.evidenceJson) }}</pre>
                }
              </section>

              <section class="panel mb-3">
                <h3 class="h6">{{ t('catalog.optimization.risk.portfolio.title') }}</h3>
                @if (portfolioPreview()?.guardrailNote) {
                  <p class="guardrail-note">{{ portfolioPreview()!.guardrailNote }}</p>
                }
                <div class="row g-2 mb-2">
                  <div class="col-md-3"><input pInputText class="w-100" [(ngModel)]="portfolioForm.fundingPoolId" [placeholder]="t('catalog.optimization.risk.portfolio.poolId')" /></div>
                  <div class="col-md-2"><input pInputText class="w-100" [(ngModel)]="portfolioForm.currencyCode" placeholder="Currency" /></div>
                  <div class="col-md-2">
                    <button pButton type="button"
                      [label]="t('catalog.optimization.risk.preview')"
                      [loading]="portfolioLoading()"
                      (click)="previewPortfolio()"></button>
                  </div>
                </div>
                @if (portfolioError()) {
                  <ain-state type="error" [title]="t('catalog.optimization.error')" [message]="portfolioError() ?? ''" />
                } @else if (portfolioPreview()) {
                  <div class="code-list mb-2">
                    <p-tag [severity]="riskSeverity(portfolioPreview()!.recommendation)" [value]="portfolioPreview()!.recommendation" />
                    @for (code of portfolioPreview()!.reasonCodes; track code) {
                      <p-tag severity="info" [value]="code" />
                    }
                  </div>
                  <p class="text-muted mb-2">
                    {{ t('catalog.optimization.risk.portfolio.concentration') }}: {{ portfolioPreview()!.concentrationRatio }}
                    · {{ t('catalog.optimization.risk.portfolio.delinquency') }}: {{ portfolioPreview()!.delinquencyRatio }}
                    · {{ t('catalog.optimization.risk.portfolio.yield') }}: {{ portfolioPreview()!.yieldProxy }}
                  </p>
                  <p class="guardrail-note">{{ portfolioPreview()!.guardrailNote }}</p>
                  <pre class="evidence-json">{{ formatJson(portfolioPreview()!.evidenceJson) }}</pre>
                }
              </section>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 0.75rem; flex-wrap: wrap; align-items: flex-end; }
    .store-field { display: flex; flex-direction: column; gap: 0.35rem; min-width: 280px; flex: 1; }
    .panel { border: 1px solid var(--surface-border); border-radius: 8px; padding: 1rem; }
    .panel-head { display: flex; justify-content: space-between; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem; }
    .code-list { display: flex; flex-wrap: wrap; gap: 0.35rem; align-items: center; }
    .json-snippet { display: block; font-size: 0.8rem; color: var(--text-color-secondary); margin-top: 0.25rem; word-break: break-word; }
    .action-row { display: flex; gap: 0.35rem; flex-wrap: wrap; }
    .evidence-json {
      background: var(--surface-ground);
      border: 1px solid var(--surface-border);
      border-radius: 6px;
      padding: 0.75rem;
      font-size: 0.8rem;
      overflow: auto;
      max-height: 240px;
      margin: 0;
    }
    .guardrail-banner,
    .guardrail-note {
      border-left: 4px solid var(--orange-500, #f59e0b);
      background: color-mix(in srgb, var(--orange-500, #f59e0b) 12%, transparent);
      padding: 0.75rem 1rem;
      border-radius: 6px;
      margin-bottom: 0.75rem;
      font-weight: 600;
    }
    .label { display: block; font-size: 0.85rem; color: var(--text-color-secondary); margin-bottom: 0.25rem; }
  `]
})
export class MarketplaceOptimizationAdminComponent implements OnInit {
  private readonly api = inject(MarketplaceOptimizationApiService);
  private readonly localization = inject(LocalizationService);

  selectedStoreId: string | null = null;
  activeTab = signal('ranking');

  rankingLoading = signal(false);
  rankingError = signal<string | null>(null);
  rankingProfiles = signal<MarketplaceRankingProfileRow[]>([]);

  recommendationLoading = signal(false);
  recommendationError = signal<string | null>(null);
  recommendationPreview = signal<MarketplaceRecommendationPreviewRow | null>(null);
  recommendationForm = {
    recommendationType: 'Newest' as RecommendationType,
    limit: 8,
    offeringId: null as string | null
  };

  reviewOfferingId: string | null = null;
  reviewFilter: ReviewFilter = 'pending';
  moderationActorReference = 'admin-operator';
  reviewsLoading = signal(false);
  reviewsError = signal<string | null>(null);
  reviews = signal<MarketplaceReviewRow[]>([]);
  filteredReviews = computed(() => {
    const rows = this.reviews();
    if (this.reviewFilter === 'pending') {
      return rows.filter(row => row.moderationStatus === 'Pending');
    }
    return rows;
  });

  promotedPlacementKey: PromotedPlacementKey = 'search';
  promotedLoading = signal(false);
  promotedError = signal<string | null>(null);
  promotedPlacements = signal<PromotedPlacementRow[]>([]);

  fraudLoading = signal(false);
  fraudError = signal<string | null>(null);
  fraudPreview = signal<FraudRiskAssessmentRow | null>(null);
  fraudForm = {
    amount: 25000,
    currencyCode: 'IRR',
    buyerCountryCode: 'IR',
    shippingCountryCode: 'IR',
    paymentCountryCode: 'IR',
    recentPaymentFailureCount: 0,
    recentRefundCount: 0,
    recentDisputeCount: 0,
    isHighValue: false
  };

  creditLoading = signal(false);
  creditError = signal<string | null>(null);
  creditPreview = signal<CreditRiskPreviewRow | null>(null);
  creditForm = {
    requestedAmount: 1200,
    currencyCode: 'IRR',
    monthlyIncome: 1800,
    existingDebt: 900,
    requestedInstallments: 6
  };

  portfolioLoading = signal(false);
  portfolioError = signal<string | null>(null);
  portfolioPreview = signal<PortfolioOptimizationPreviewRow | null>(null);
  portfolioForm = {
    fundingPoolId: '',
    currencyCode: 'IRR'
  };

  readonly recommendationTypeOptions = [
    { label: 'Newest', value: 'Newest' as RecommendationType },
    { label: 'BestSelling', value: 'BestSelling' as RecommendationType },
    { label: 'RelatedOfferings', value: 'RelatedOfferings' as RecommendationType },
    { label: 'CategoryTrending', value: 'CategoryTrending' as RecommendationType },
    { label: 'Personalized', value: 'Personalized' as RecommendationType }
  ];

  readonly reviewFilterOptions = [
    { labelKey: 'catalog.optimization.reviews.filterPending', value: 'pending' as ReviewFilter },
    { labelKey: 'catalog.optimization.reviews.filterAll', value: 'all' as ReviewFilter }
  ];

  readonly placementKeyOptions = [
    { labelKey: 'catalog.optimization.promoted.search', value: 'search' as PromotedPlacementKey },
    { labelKey: 'catalog.optimization.promoted.home', value: 'home' as PromotedPlacementKey }
  ];

  ngOnInit(): void {
    // Store selector auto-selects when possible; tab data loads on store change.
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  onStoreChange(): void {
    this.reloadActiveTab();
  }

  onTabChange(tab: string | number | undefined): void {
    const value = String(tab ?? 'ranking');
    this.activeTab.set(value);
    this.reloadActiveTab();
  }

  reloadActiveTab(): void {
    if (!this.selectedStoreId) {
      return;
    }

    switch (this.activeTab()) {
      case 'ranking':
        this.loadRanking();
        break;
      case 'recommendations':
        this.recommendationPreview.set(null);
        this.recommendationError.set(null);
        break;
      case 'reviews':
        this.loadReviews();
        break;
      case 'promoted':
        this.loadPromoted();
        break;
      case 'risk':
        this.fraudPreview.set(null);
        this.creditPreview.set(null);
        this.portfolioPreview.set(null);
        this.fraudError.set(null);
        this.creditError.set(null);
        this.portfolioError.set(null);
        break;
    }
  }

  loadRanking(): void {
    if (!this.selectedStoreId) {
      return;
    }

    this.rankingLoading.set(true);
    this.rankingError.set(null);
    this.api.listRankingProfiles(this.selectedStoreId).subscribe({
      next: rows => {
        this.rankingProfiles.set(rows);
        this.rankingLoading.set(false);
      },
      error: err => {
        this.rankingError.set(err?.message ?? 'load_failed');
        this.rankingProfiles.set([]);
        this.rankingLoading.set(false);
      }
    });
  }

  previewRecommendations(): void {
    if (!this.selectedStoreId) {
      return;
    }

    this.recommendationLoading.set(true);
    this.recommendationError.set(null);
    this.api.previewRecommendations({
      storeId: this.selectedStoreId,
      recommendationType: this.recommendationForm.recommendationType,
      offeringId: this.recommendationForm.offeringId || null,
      limit: Number(this.recommendationForm.limit) || 8,
      requestPersonalized: this.recommendationForm.recommendationType === 'Personalized'
    }).subscribe({
      next: preview => {
        this.recommendationPreview.set(preview);
        this.recommendationLoading.set(false);
      },
      error: err => {
        this.recommendationError.set(err?.message ?? 'preview_failed');
        this.recommendationPreview.set(null);
        this.recommendationLoading.set(false);
      }
    });
  }

  loadReviews(): void {
    if (!this.selectedStoreId || !this.reviewOfferingId) {
      this.reviews.set([]);
      return;
    }

    this.reviewsLoading.set(true);
    this.reviewsError.set(null);
    this.api.listReviews(this.selectedStoreId, this.reviewOfferingId, false).subscribe({
      next: rows => {
        this.reviews.set(rows);
        this.reviewsLoading.set(false);
      },
      error: err => {
        this.reviewsError.set(err?.message ?? 'load_failed');
        this.reviews.set([]);
        this.reviewsLoading.set(false);
      }
    });
  }

  moderateReview(row: MarketplaceReviewRow, status: 'Approved' | 'Rejected'): void {
    const actor = this.moderationActorReference.trim();
    if (!actor) {
      this.reviewsError.set('actor_reference_required');
      return;
    }

    this.api.moderateReview(row.id, {
      status,
      actorReference: actor,
      notes: status === 'Approved' ? 'approved_by_admin' : 'rejected_by_admin'
    }).subscribe({
      next: () => this.loadReviews(),
      error: err => this.reviewsError.set(err?.message ?? 'moderation_failed')
    });
  }

  loadPromoted(): void {
    if (!this.selectedStoreId) {
      return;
    }

    this.promotedLoading.set(true);
    this.promotedError.set(null);
    this.api.listPromotedPlacements(this.selectedStoreId, this.promotedPlacementKey).subscribe({
      next: rows => {
        this.promotedPlacements.set(rows);
        this.promotedLoading.set(false);
      },
      error: err => {
        this.promotedError.set(err?.message ?? 'load_failed');
        this.promotedPlacements.set([]);
        this.promotedLoading.set(false);
      }
    });
  }

  previewFraud(): void {
    if (!this.selectedStoreId) {
      return;
    }

    this.fraudLoading.set(true);
    this.fraudError.set(null);
    this.api.previewFraudRisk({
      storeId: this.selectedStoreId,
      amount: Number(this.fraudForm.amount),
      currencyCode: this.fraudForm.currencyCode,
      buyerCountryCode: this.fraudForm.buyerCountryCode,
      shippingCountryCode: this.fraudForm.shippingCountryCode,
      paymentCountryCode: this.fraudForm.paymentCountryCode,
      recentPaymentFailureCount: Number(this.fraudForm.recentPaymentFailureCount),
      recentRefundCount: Number(this.fraudForm.recentRefundCount),
      recentDisputeCount: Number(this.fraudForm.recentDisputeCount),
      isHighValue: this.fraudForm.isHighValue,
      correlationId: `admin-fraud-${Date.now()}`
    }).subscribe({
      next: preview => {
        this.fraudPreview.set(preview);
        this.fraudLoading.set(false);
      },
      error: err => {
        this.fraudError.set(err?.message ?? 'preview_failed');
        this.fraudPreview.set(null);
        this.fraudLoading.set(false);
      }
    });
  }

  previewCredit(): void {
    this.creditLoading.set(true);
    this.creditError.set(null);
    this.api.previewCreditRisk({
      creditApplicationId: crypto.randomUUID(),
      customerAccountId: crypto.randomUUID(),
      requestedAmount: Number(this.creditForm.requestedAmount),
      currencyCode: this.creditForm.currencyCode,
      monthlyIncome: Number(this.creditForm.monthlyIncome),
      existingDebt: Number(this.creditForm.existingDebt),
      requestedInstallments: Number(this.creditForm.requestedInstallments)
    }).subscribe({
      next: preview => {
        this.creditPreview.set(preview);
        this.creditLoading.set(false);
      },
      error: err => {
        this.creditError.set(err?.message ?? 'preview_failed');
        this.creditPreview.set(null);
        this.creditLoading.set(false);
      }
    });
  }

  previewPortfolio(): void {
    const poolId = this.portfolioForm.fundingPoolId.trim();
    if (!poolId) {
      this.portfolioError.set('funding_pool_required');
      return;
    }

    this.portfolioLoading.set(true);
    this.portfolioError.set(null);
    this.api.previewPortfolioOptimization({
      fundingPoolId: poolId,
      currencyCode: this.portfolioForm.currencyCode
    }).subscribe({
      next: preview => {
        this.portfolioPreview.set(preview);
        this.portfolioLoading.set(false);
      },
      error: err => {
        this.portfolioError.set(err?.message ?? 'preview_failed');
        this.portfolioPreview.set(null);
        this.portfolioLoading.set(false);
      }
    });
  }

  formatJson(raw: string): string {
    if (!raw?.trim()) {
      return '';
    }

    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }

  moderationSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (status === 'Approved') return 'success';
    if (status === 'Pending') return 'warn';
    if (status === 'Rejected') return 'danger';
    return 'secondary';
  }

  riskSeverity(recommendation: string): 'success' | 'warn' | 'danger' | 'secondary' {
    const normalized = recommendation.trim().toLowerCase();
    if (normalized === 'allow') return 'success';
    if (normalized === 'review') return 'warn';
    if (normalized === 'block') return 'danger';
    return 'secondary';
  }
}
